<template>
  <a href="#" class="tu-school-card tu-browse-card" :aria-label="`View school: ${school.name}`" @click.prevent="$emit('view', school)">
    <div class="tsc-media" :class="{ 'tsc-media--fallback': !campus }">
      <img v-if="campus" :src="campus" alt="" loading="lazy" decoding="async" @error="campusFailed = true" />
      <tu-destination-icon v-else kind="schools" aria-hidden="true" />
    </div>
    <!-- Portal only: save to favorites, and the Enrolled pill for schools on a path the student started -->
    <div class="tsc-top">
      <button
        type="button"
        class="tsc-fav"
        :class="{ 'is-on': school.fav_status }"
        :aria-label="school.fav_status ? 'Remove from favorites' : 'Save to favorites'"
        :aria-pressed="school.fav_status ? 'true' : 'false'"
        @click.stop.prevent="$emit('toggle-fav', school)"
      >
        <md-icon>{{ school.fav_status ? 'favorite' : 'favorite_border' }}</md-icon>
      </button>
      <span v-if="unlocked" class="tsc-unlocked"><md-icon>check_circle</md-icon> Enrolled</span>
    </div>
    <div class="tsc-body">
      <div class="tsc-identity">
        <span class="tsc-logo" aria-hidden="true">
          <img v-if="logo && !logoFailed" :src="logo" alt="" loading="lazy" @error="logoFailed = true" />
          <span v-else>{{ initial }}</span>
        </span>
        <div class="tsc-copy">
          <h3 :title="school.name">{{ school.name }}</h3>
          <p v-if="location" class="tu-place"><tu-ui-icon name="location" :size="14" />{{ location }}</p>
        </div>
      </div>
      <dl class="tsc-facts">
        <div><dt>Type</dt><dd>{{ typeLabel }}</dd></div>
        <div><dt>Programs</dt><dd>{{ programs.length }}</dd></div>
        <div><dt>State</dt><dd>{{ stateCode }}</dd></div>
      </dl>
      <div v-if="programLabels.length" class="tsc-program-badges" aria-label="Available programs">
        <span v-for="label in programLabels" :key="label" class="tu-credential-badge">{{ label }}</span>
        <span v-if="morePrograms" class="tsc-more-programs">+{{ morePrograms }} more</span>
      </div>
      <p v-else-if="summary" class="tsc-summary">{{ summary }}</p>
      <span class="tu-card-action"><span class="tu-card-action-label">View school</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></span>
    </div>
  </a>
</template>

<script>
import TuUiIcon from '@/tu/components/UiIcon.vue';
import TuDestinationIcon from '@/tu/components/DestinationIcon.vue';
import { stateAbbr } from '@/tu/utils/stateAbbr';

const TYPE_LABEL = { university: 'University', community_college: '2 year', technical_college: 'Technical', career_college: 'Career', hospital_school: 'Hospital' };

/**
 * School card for the student portal. The same card as transitionu.health
 * (components/Tu/SchoolCard.vue): cover, logo + name + location, a Type / Programs / State
 * panel, the programs as credential badges, one "View school" action. The portal adds the
 * favorite button and the Enrolled pill.
 */
export default {
  name: 'SchoolCard',
  components: { TuUiIcon, TuDestinationIcon },
  props: {
    school: { type: Object, required: true },
    image: { type: String, default: '' },
    // The student holds a pathway enrollment that includes this school.
    unlocked: { type: Boolean, default: false },
  },
  data() {
    return { logoFailed: false, campusFailed: false };
  },
  computed: {
    programs() {
      const list = Array.isArray(this.school.program_badges) ? this.school.program_badges : [];
      const seen = new Set();
      return list.filter(p => {
        const key = p && (p.abbr || p.title);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    programLabels() {
      return this.programs.slice(0, 4).map(p => p.abbr || p.title);
    },
    morePrograms() {
      return Math.max(0, this.programs.length - this.programLabels.length);
    },
    typeLabel() {
      const t = this.school.institution_type || this.school.institutionType || '';
      return TYPE_LABEL[t] || (t ? String(t).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : 'School');
    },
    stateCode() {
      return stateAbbr(this.school.state) || this.school.state || '';
    },
    campus() {
      if (this.campusFailed) return '';
      return this.image || this.school.campus_img_url || '';
    },
    logo() {
      return this.school.small_logo_url || this.school.logo_url || '';
    },
    location() {
      return [this.school.city, this.school.state].filter(Boolean).join(', ');
    },
    initial() {
      return String(this.school.name || '?').trim().charAt(0).toUpperCase() || '?';
    },
    summary() {
      const bio = String(this.school.short_bio || '').trim();
      if (!bio) return '';
      return bio.length > 90 ? `${bio.slice(0, 87)}…` : bio;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Verbatim from transition_uni_listings components/Tu/SchoolCard.vue */
.tu-school-card { position: relative; display: flex; flex-direction: column; min-width: 0; height: 100%; overflow: hidden; border-radius: 12px; background: var(--tu-surface); color: var(--tu-ink) !important; border: 1px solid rgba(24, 24, 24, 0.1); box-sizing: border-box; box-shadow: none; text-decoration: none !important; transition: transform 0.16s ease, box-shadow 0.16s ease; }
.tu-school-card:hover { transform: translateY(-1px); box-shadow: var(--tu-shadow-lift); }
.tu-school-card:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 4px; }
.tsc-media { height: 148px; flex: none; background: var(--tu-surface-2); }
.tsc-media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tsc-media--fallback { display: grid; place-items: center; background: var(--tu-blue-tint); color: var(--tu-blue); }
.tsc-media--fallback ::v-deep svg { width: 44px; height: 44px; opacity: 0.55; }
.tsc-body { display: flex; flex-direction: column; flex: 1; gap: 14px; padding: 22px; }
.tsc-identity { min-height: 0; display: flex; align-items: center; gap: 12px; }
.tsc-logo { display: flex; align-items: center; justify-content: center; flex: 0 0 44px; height: 44px; border-radius: 10px; background: #fff; box-shadow: inset 0 0 0 1px var(--tu-line); overflow: hidden; font-weight: 800; color: var(--tu-blue-deep); }
.tsc-logo img { width: 100%; height: 100%; object-fit: contain; padding: 4px; box-sizing: border-box; }
.tsc-copy { min-width: 0; }
.tsc-copy h3 { margin: 0 0 3px; font-size: 18px; font-weight: 700; line-height: 1.22; min-height: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere; color: var(--tu-ink); }
.tsc-copy p, .tsc-summary { margin: 0; font-size: 13px; line-height: 1.5; color: var(--tu-ink-soft); }
.tsc-copy p { display: flex; align-items: center; gap: 4px; }
.tsc-facts { display: grid; grid-template-columns: 1.3fr 1fr 0.9fr; gap: 10px; margin: 0; padding: 14px 16px; border-radius: 12px; background: var(--tu-blue-tint); }
.tsc-facts > div { min-width: 0; }
.tsc-facts dt { margin: 0; font-size: 12.5px; font-weight: 500; color: var(--tu-ink-soft); }
.tsc-facts dd { margin: 4px 0 0; font-weight: 700; font-size: 16px; color: var(--tu-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tsc-program-badges { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 0; }
.tsc-program-badges .tu-credential-badge { background: var(--tu-blue-tint); color: var(--tu-blue-deep); border-color: transparent; }
.tsc-more-programs { font-size: 12px; font-weight: 700; color: var(--tu-ink-soft); }
.tsc-body > .tu-card-action { border-top: 1px solid var(--tu-line-soft); padding-top: 14px; }
@media (prefers-reduced-motion: reduce) { .tu-school-card { transition: none; } .tu-school-card:hover { transform: none; } }

/* Portal only */
.tsc-top { position: static; height: 0; }
.tsc-fav {
  position: absolute; top: 15px; right: 12px; z-index: 2; width: 36px; height: 36px; border: 0; border-radius: 50%;
  background: rgba(255, 255, 255, 0.95); box-shadow: 0 1px 3px rgba(24, 24, 24, 0.18); color: var(--tu-ink-mute);
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: transform 0.15s ease, color 0.15s ease;
  .md-icon { font-size: 20px; color: inherit; }
  &:hover { transform: scale(1.06); color: #e0245e; }
  &.is-on { color: #e0245e; }
}
.tsc-unlocked {
  position: absolute; left: 14px; top: 17px; z-index: 2; display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.95); box-shadow: 0 1px 3px rgba(24, 24, 24, 0.12); color: var(--tu-green); font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
  .md-icon { font-size: 14px; color: var(--tu-green); }
}
</style>
